import { useCommentEditingStore } from "@/features/controls/stores/comment-editing.store";
import type { ControlResultActivityListItem } from "@/models/control-result-activity.model";
import { showAlertCallout } from "@/shared/alerts";
import { ControlResultActivityFactory } from "@/shared/factories/control-result-activity.factory";
import { useEditComment } from "@/spz-api/control-result-comments/control-result-comments";
import { getGetControlResultActivitiesQueryKey } from "@/spz-api/control-results/control-results";
import type { RichTextDto } from "@/spz-api/model/richTextDto";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export const useCommentEditing = (
  controlId: string,
  controlResultId: string,
) => {
  const {
    editingCommentId,
    editedContent,
    setEditingComment,
    resetEditing,
    updateContent,
  } = useCommentEditingStore();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const mutation = useEditComment({
    mutation: {
      onMutate: async (variables) => {
        const { commentId, data } = variables;

        // First user feedback, to remove editing state
        resetEditing();

        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: getGetControlResultActivitiesQueryKey(
            controlId,
            controlResultId,
          ),
        });

        // Snapshot the previous value
        const previousActivities = queryClient.getQueryData<
          ControlResultActivityListItem[]
        >(getGetControlResultActivitiesQueryKey(controlId, controlResultId));

        // Optimistically update the activity list
        queryClient.setQueryData<ControlResultActivityListItem[]>(
          getGetControlResultActivitiesQueryKey(controlId, controlResultId),
          (old) => {
            if (!old) return old;

            return old.map((activity) => {
              if (activity.comment && activity.comment.id === commentId) {
                // Use factory to create updated instance
                return ControlResultActivityFactory.withUpdatedComment(
                  activity,
                  {
                    id: activity.comment.id,
                    content:
                      data.content as unknown as typeof activity.comment.content,
                    hasBeenUpdated: true,
                    updatedAt: new Date(),
                  },
                );
              }
              return activity;
            });
          },
        );

        // Return context with snapshot for rollback
        return { previousActivities };
      },
      onError: (_, variables, context) => {
        // Rollback to the previous value on error
        if (context?.previousActivities) {
          queryClient.setQueryData<ControlResultActivityListItem[]>(
            getGetControlResultActivitiesQueryKey(controlId, controlResultId),
            context.previousActivities,
          );
        }

        showAlertCallout({
          type: "critical",
          title: t("error_comment_update"),
        });
      },
      onSettled: () => {
        // Always refetch after error or success to ensure consistency
        queryClient.invalidateQueries({
          queryKey: getGetControlResultActivitiesQueryKey(
            controlId,
            controlResultId,
          ),
        });
      },
    },
  });

  const handleEdit = (commentId: string, content: unknown) => {
    if (!content) {
      return;
    }
    setEditingComment(commentId, content as RichTextDto);
  };

  const handleValidate = async (commentId: string) => {
    mutation.mutate({
      controlId,
      controlResultId,
      commentId,
      data: { content: editedContent },
    });
  };

  return {
    isEditing: editingCommentId !== null,
    editingCommentId,
    editedContent,
    handleEdit,
    handleCancel: resetEditing,
    handleValidate,
    updateContent,
  };
};
